import React, { useState, useEffect, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import {
  createResource,
  updateResource,
  deleteResource,
} from "../store/thunks/resourceThunk";
import { logError, logInfo } from "../utils/logger";
import SearchDropdown from "../applicationUi/components/SearchDropdown";

function ResourceFormModal({
  isOpen,
  onClose,
  onSave,
  mode = "add",
  resource = null,
}) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const [formData, setFormData] = useState({
    name: "",
    type: "Room",
    description: "",
    status: "active",
  });
  const [selectedClinic, setSelectedClinic] = useState(null);

  console.log("resource", resource); // Debugging log

  // Populate form when editing an existing resource
  useEffect(() => {
    if (mode === "edit" && resource) {
      setFormData({
        name: resource.name || "",
        type: resource.type || "Room",
        description: resource.description || "",
        status: resource.status || "active",
      });

      if (resource.clinic) {
        setSelectedClinic(resource.clinic);
      }
    } else {
      setFormData({
        name: "",
        type: "Room",
        description: "",
        status: "active",
      });
      setSelectedClinic(null);
    }
  }, [mode, resource]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClinicSelection = (clinic) => {
    setSelectedClinic(clinic);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Use selected clinic if available, otherwise use default location
      const clinicId = selectedClinic?._id;

      if (mode === "edit") {
        logInfo("Updating resource", { resourceId: resource._id });
        await dispatch(
          updateResource({
            clinicId: clinicId,
            resourceId: resource._id,
            resourceData: formData,
          })
        ).unwrap();
        logInfo("Resource updated successfully", { resourceId: resource._id });
      } else {
        logInfo("Creating new resource");
        await dispatch(
          createResource({
            clinicId: clinicId,
            resourceData: {
              ...formData,
              clinic: clinicId,
              createdBy: user._id,
            },
          })
        ).unwrap();
        logInfo("Resource created successfully");
      }
      onSave();
      onClose();
    } catch (error) {
      logError(
        mode === "edit"
          ? "Failed to update resource"
          : "Failed to create resource",
        {
          error,
          resourceId: resource?._id,
        }
      );
      console.error("Error saving resource:", error);
    }
  };

  const handleDelete = async () => {
    if (!resource?._id) {
      console.error("Cannot delete resource: No resource ID provided");
      logError("Cannot delete resource: No resource ID provided");
      return;
    }

    console.log("Deleting resource:", resource._id); // Debugging log

    try {
      // Use selected clinic if available, otherwise use default location
      const clinicId = selectedClinic?._id || user.defaultLocation;

      logInfo("Deleting resource", { resourceId: resource._id });
      await dispatch(
        deleteResource({
          clinicId: clinicId,
          resourceId: resource._id,
        })
      ).unwrap();
      logInfo("Resource deleted successfully", { resourceId: resource._id });

      onSave();
      onClose();
    } catch (error) {
      logError("Failed to delete resource", {
        error,
        resourceId: resource._id,
      });
      console.error("Error deleting resource:", error);
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
              <Dialog.Panel className="relative transform overflow-hidden rounded-sm bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
                {/* Close Button */}
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-sm bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={onClose}>
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                {/* Form Title */}
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <Dialog.Title
                      as="h3"
                      className="text-base font-semibold leading-6 text-gray-900">
                      {mode === "add" ? "Add New Resource" : "Edit Resource"}
                    </Dialog.Title>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                      {/* Clinic Search */}
                      <div>
                        {/* <label
                          htmlFor="clinic"
                          className="block text-sm font-medium leading-6 text-gray-900 mb-1">
                          Clinic
                        </label> */}
                        <p className="text-sm font-medium text-muted-foreground mb-2">
                          Clinic
                        </p>
                        <SearchDropdown
                          prefix=":clinic:"
                          displayFields={[
                            "name",
                            "phoneNumber",
                            "address.street",
                          ]}
                          onSelection={handleClinicSelection}
                          placeholder="Search for a clinic"
                          initialSelection={selectedClinic}
                        />
                        {selectedClinic && (
                          <div className="mt-2 text-sm text-gray-700">
                            Selected: {selectedClinic.name}
                            {selectedClinic.address?.street &&
                              ` - ${selectedClinic.address.street}`}
                          </div>
                        )}
                      </div>

                      {/* Name */}
                      <div>
                        <label
                          htmlFor="name"
                          className="block text-sm font-medium leading-6 text-gray-900">
                          Name
                        </label>
                        <input
                          type="text"
                          name="name"
                          id="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>

                      {/* Type */}
                      <div>
                        <label
                          htmlFor="type"
                          className="block text-sm font-medium leading-6 text-gray-900">
                          Type
                        </label>
                        <select
                          id="type"
                          name="type"
                          value={formData.type}
                          onChange={handleChange}
                          className="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                          <option value="Room">Room</option>
                          <option value="Equipment">Equipment</option>
                          <option value="Testing Material">
                            Testing Material
                          </option>
                          <option value="Assistive Device">
                            Assistive Device
                          </option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      {/* Status */}
                      <div>
                        <label
                          htmlFor="status"
                          className="block text-sm font-medium leading-6 text-gray-900">
                          Status
                        </label>
                        <select
                          id="status"
                          name="status"
                          value={formData.status}
                          onChange={handleChange}
                          className="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                          <option value="maintenance">Maintenance</option>
                        </select>
                      </div>

                      {/* Description */}
                      <div>
                        <label
                          htmlFor="description"
                          className="block text-sm font-medium leading-6 text-gray-900">
                          Description
                        </label>
                        <textarea
                          id="description"
                          name="description"
                          rows={3}
                          value={formData.description}
                          onChange={handleChange}
                          className="mt-1 block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>

                      {/* Buttons */}
                      <div className="mt-6 flex justify-end gap-x-3">
                        <button
                          type="submit"
                          className="rounded-sm bg-indigo-600 px-3 py-2 text-sm font-semibold text-white">
                          {mode === "add"
                            ? "Create Resource"
                            : "Update Resource"}
                        </button>
                        {mode === "edit" && (
                          <button
                            type="button"
                            onClick={handleDelete}
                            className="rounded-sm bg-red-600 px-3 py-2 text-sm font-semibold text-white">
                            Delete
                          </button>
                        )}
                      </div>
                    </form>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default ResourceFormModal;
